import React from 'react';
import { milliseconds } from 'date-fns';
import { Text } from '@applatform/design-system';
import { useClientSWR } from 'context/AuthContext/AuthContext';
import { SkeletonLine } from 'components/V1/Skeleton/Skeleton';
import { v2Colors } from 'theme';

interface PlatformName {
  name: string;
}

/**
 * The marketplace's name. Shared so a caller can lay out around it (e.g. drop
 * a separator when there is no name); SWR dedupes the request.
 */
export function usePlatformName() {
  const { data, error } = useClientSWR<PlatformName>('platform/name', {
    dedupingInterval: milliseconds({ minutes: 15 }),
  });

  return {
    name: data?.name || undefined,
    isLoading: !data && !error,
  };
}

function MarketplaceName() {
  const { name, isLoading } = usePlatformName();

  if (isLoading) {
    return (
      <SkeletonLine
        data-testid="marketplace-name-skeleton"
        css={{ width: '140px', height: '14px' }}
      />
    );
  }

  if (!name) {
    return null;
  }

  return (
    <Text
      as="span"
      css={{
        fontSize: '13.5px',
        fontWeight: 650,
        color: v2Colors.ink2,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {name}
    </Text>
  );
}

export default MarketplaceName;
